import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useToast } from '../../context';
import { useLang, useAutoTranslate, SOURCE_LANG } from '../../i18n';
import { useChecklist } from '../../hooks/useChecklist';
import { useLatest } from '../../hooks/useLatest';
import { useSaveFailed } from '../../hooks/useSaveFailed';
import { normalizeName } from '../../logic/names';
import { fmtDate, localDateISO } from '../../logic/dates';
import { learnedStores, knownStores, groupByStore } from '../../logic/stores';
import { orderFrecency } from '../../logic/catalogs';
import { splitNames, planChecklistAdds, checklistCatalog, hiddenNameKeys } from '../../logic/checklists';
import { DEFAULT_STORES, NO_STORE_LABEL } from '../../config/checklists';
import {
  addChecklistItem, addToChecklist, removeChecklistItem, hideChecklistItem, unhideChecklistItem,
  completeChecklistItems, reopenChecklistItems, seedChecklist, setChecklistNeeded,
} from '../../data/checklists';
import { keepInView } from '../shared/dom';
import { PreviewModal } from '../shared/PreviewModal';
import { Segmented } from '../shared/Toggles';
import { Loading } from '../shared/EmptyState';
import { ChecklistRow } from './ChecklistRow';
import { ChecklistLog } from './ChecklistLog';
import { ChecklistSummary } from './ChecklistSummary';

const NO_NAMES = [];
const byOrder = (a, b) => (a.order || 0) - (b.order || 0) || a.name.localeCompare(b.name);

// Close the on-screen keyboard (esp. iPad) once something's been added / picked.
const dismissKeyboard = () => { const el = document.activeElement; if (el && typeof el.blur === 'function') el.blur(); };

/**
 * A standing checklist tab (Shopping · Backup · Cutting): the working list (left, by
 * store for Shopping) and the catalog to pick from (right), plus its History — a
 * Summary rollup and the raw log. `config` = one of config/checklists.js CHECKLISTS.
 */
export function ChecklistTab({ config, author }) {
  const { t, ln, lang } = useLang();
  const notify = useToast();
  const failed = useSaveFailed();
  const verb = t(`checklist.verb.${config.id}`);
  const label = t(`checklist.label.${config.id}`);
  const toDoLabel = config.id === 'cutting' ? t('checklist.toCut') : t('checklist.toBuy');
  const { items, log, loading, confirmedEmpty } = useChecklist(config.id);
  useAutoTranslate(useMemo(
    () => (lang === SOURCE_LANG ? NO_NAMES : [...new Set([...items.map(i => i.name), ...log.map(l => l.name)])]),
    [lang, items, log],
  ));

  const [search, setSearch] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [histView, setHistView] = useState('summary');   // summary | log
  const [showPreview, setShowPreview] = useState(false);
  const [addingStore, setAddingStore] = useState(null);   // which store group's inline add is open
  const [storeAddText, setStoreAddText] = useState('');

  // Seed the printed-form template once, when the server confirms the list has never had
  // an item. (An empty first load from the cache — offline, over quota — or a failed one
  // must not: the template would be added again on top of the real list once it synced.
  // Deleting items only hides them, so deleting everything doesn't bring it back either.)
  const seeded = useRef(false);
  useEffect(() => {
    if (confirmedEmpty && !seeded.current && config.seeds && config.seeds.length) {
      seeded.current = true;
      seedChecklist(config.id, config.seeds).catch(failed);
    }
  }, [confirmedEmpty, config, failed]);

  const active = useMemo(() => items.filter(i => i.needed).sort(byOrder), [items]);
  const activeNames = useMemo(() => new Set(active.map(i => normalizeName(i.name))), [active]);
  // "Due to reorder" leaves out what's on the list already and what was deleted from the checklist.
  const notDue = useMemo(() => new Set([...activeNames, ...hiddenNameKeys(items)]), [activeNames, items]);
  const frecency = useMemo(() => orderFrecency(log, localDateISO()), [log]);   // name → how often + how recently
  const catalog = useMemo(() => checklistCatalog(items, log, frecency, search), [items, log, frecency, search]);
  const learned = useMemo(() => learnedStores(log), [log]);                      // name → store from history
  // The store autosuggest: same stores → same array, so the memoized rows don't re-render.
  const storeList = useMemo(() => knownStores(items, log, DEFAULT_STORES), [items, log]);
  const storesKey = storeList.join('\n');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `storeList` is covered by storesKey
  const stores = useMemo(() => storeList, [storesKey]);

  // Put names (one, or comma / newline separated) on the active list in one write —
  // existing catalog items are re-flagged, new ones created. Shopping files each under a
  // store: `forcedStore` (a store group's ＋), else the one it was last bought from.
  const addNames = (text, forcedStore) => {
    const today = localDateISO();
    const storeFor = name => (!config.hasStore ? ''
      : (forcedStore !== undefined ? forcedStore : (learned.get(normalizeName(name)) || '')));
    const plan = planChecklistAdds({ names: splitNames(text), items, storeFor, today });
    if (!plan.creates.length && !plan.updates.length) return;
    addToChecklist({ list: config.id, ...plan, firstOrder: (items.length + 1) * 10, addedBy: author, today }).saved.catch(failed);
    dismissKeyboard();
  };
  const sq = normalizeName(search);
  const canAddSearch = !!sq && !catalog.some(i => normalizeName(i.name) === sq);
  const addSearch = () => { if (search.trim()) { addNames(search.trim()); setSearch(''); } };
  const addToStore = storeName => {
    addNames(storeAddText, storeName === NO_STORE_LABEL ? '' : storeName);
    setStoreAddText(''); setAddingStore(null);
  };

  // Catalog tap: a history-only name comes back as a saved item on the list; a saved
  // item toggles on / off the list (filling its usual store when it goes on).
  const pick = item => {
    if (item.ghost) { addNames(item.name); return; }
    const on = !item.needed;
    const store = on && config.hasStore && !item.store ? learned.get(normalizeName(item.name)) : '';
    setChecklistNeeded(item.id, on, { store }).catch(failed);
    dismissKeyboard();
  };

  // One tap logs items as done today and clears them off the list; undo deletes the log
  // rows and puts them back. Stable (refs), so the memoized rows don't re-render.
  const latest = useLatest({ author, notify, t, ln, verb, failed });
  const complete = useCallback((list, message) => {
    const { author: by, notify: say, failed: onError } = latest.current;
    const { logIds, saved } = completeChecklistItems(list.map(i => ({ ...i, done: true })), { date: localDateISO(), by });
    saved.catch(onError);
    say(message, () => reopenChecklistItems({ itemIds: list.map(i => i.id), logIds }).catch(onError));
  }, [latest]);
  const completeItem = useCallback(item => {
    const { t: tr, ln: name, verb: v } = latest.current;
    complete([item], tr('checklist.notifyDone', { verb: v, name: name(item.name) }));
  }, [complete, latest]);
  const completeAll = () => { if (active.length) complete(active, t('checklist.notifyAll', { verb, n: active.length })); };
  const removeFromList = useCallback(item => setChecklistNeeded(item.id, false).catch(latest.current.failed), [latest]);
  // ✕ in the catalog deletes the item from the checklist: it's hidden (not deleted — its
  // name would come straight back from the history), and a history-only name gets a hidden
  // item of its own. Adding the name again brings it back; undo restores it as it was.
  const deleteFromCatalog = item => {
    const say = () => t('checklist.notifyRemoved', { name: ln(item.name) });
    if (item.ghost) {
      const made = addChecklistItem({ list: config.id, name: item.name, hidden: true, addedBy: author });
      made.saved.catch(failed);
      notify(say(), () => removeChecklistItem(made.id).catch(failed));
      return;
    }
    hideChecklistItem(item.id).catch(failed);
    notify(say(), () => unhideChecklistItem(item.id, item).catch(failed));
  };
  // Summary → "due to reorder" tap: straight back on the list (with its usual store).
  const reorder = row => {
    addNames(row.name);
    notify(t('audit.notifyReordered', { name: ln(row.name) }));
  };

  // Preview / print: Shopping is grouped by store; the others are one list.
  const byStore = useMemo(() => (config.hasStore ? groupByStore(active) : null), [config.hasStore, active]);
  const previewSections = useMemo(() => {
    const rows = list => list.map(r => ({ name: ln(r.name), meta: r.qty }));
    if (byStore) return byStore.map(([storeName, list]) => ({ key: storeName, heading: `🏪 ${storeName}`, count: list.length, rows: rows(list) }));
    return active.length ? [{ key: config.id, heading: `${config.icon} ${toDoLabel}`, count: active.length, rows: rows(active) }] : [];
  }, [byStore, active, config, toDoLabel, ln]);

  const rowList = list => (
    <div className="do-checklist__items">
      {list.map(item => (
        <ChecklistRow key={item.id} item={item} config={config} stores={stores} onComplete={completeItem} onRemove={removeFromList} />
      ))}
    </div>
  );

  return (
    <div className="do-checklist" style={{ '--cc': config.color }}>
      <div className="do-checklist__bar">
        {config.cadence && <span className="do-checklist__cadence">📆 {t(`checklist.cadence.${config.id}`)}</span>}
        <span className="do-checklist__spacer" />
        <button className="do-shopbtn" onClick={() => setShowPreview(true)}
          title={config.hasStore ? t('checklist.previewStore') : t('checklist.preview')}>{t('common.preview')}</button>
        <button className={`do-focus${showHistory ? ' do-focus--on' : ''}`} onClick={() => setShowHistory(h => !h)}>
          {showHistory ? t('checklist.backToList') : t('checklist.history', { n: log.length })}
        </button>
      </div>
      <p className="do-checklist__intro">{t(`checklist.intro.${config.id}`)}</p>
      {showPreview && (
        <PreviewModal title={`${config.icon} ${label}`} subtitle={fmtDate(localDateISO())}
          sections={previewSections} onClose={() => setShowPreview(false)} notify={notify} />
      )}

      {showHistory ? (
        <>
          <Segmented className="do-hist-views" value={histView} onChange={setHistView}
            options={[['summary', t('checklist.viewSummary')], ['log', t('checklist.viewLog', { n: log.length })]]} />
          {histView === 'summary'
            ? <ChecklistSummary config={config} log={log} skipKeys={notDue} onReorder={reorder} />
            : <ChecklistLog config={config} log={log} />}
        </>
      ) : (
        <div className="do-checklist__panes">
          {/* Left — the working list */}
          <div className="do-checklist__active">
            <div className="do-checklist__pane-head">
              <span className="do-checklist__pane-title">{config.icon} {toDoLabel}</span>
              <span className="do-checklist__pane-count">{active.length}</span>
            </div>
            {active.length > 0 && (
              <div className="do-checklist__bulk">
                <button className="do-checklist__bulk-btn do-checklist__bulk-btn--go" onClick={completeAll}
                  title={t('checklist.completeAllTitle', { n: active.length, verb })}>
                  {t('checklist.completeAll', { verb, n: active.length })}
                </button>
              </div>
            )}
            {loading ? <Loading label={t('common.loading')} /> : active.length === 0 ? (
              <div className="do-checklist__empty">
                <span className="do-checklist__empty-icon">{config.icon}</span>
                <p>{t('checklist.emptyActiveTitle')}<br />{t('checklist.emptyActiveHint')}</p>
              </div>
            ) : byStore ? byStore.map(([storeName, list]) => (
              <StoreGroup key={storeName} storeName={storeName} count={list.length}
                adding={addingStore === storeName} text={storeAddText} onText={setStoreAddText}
                onToggleAdd={() => { setAddingStore(a => (a === storeName ? null : storeName)); setStoreAddText(''); }}
                onAdd={() => addToStore(storeName)} onCancel={() => { setAddingStore(null); setStoreAddText(''); }}>
                {rowList(list)}
              </StoreGroup>
            )) : rowList(active)}
          </div>

          {/* Right — the checklist to pick from */}
          <div className="do-checklist__catalog">
            <div className="do-checklist__pane-head">
              <span className="do-checklist__pane-title">{t('checklist.paneChecklist')}</span>
              <input className="do-checklist__search" placeholder={t('checklist.searchAdd')}
                value={search} onChange={e => setSearch(e.target.value)} onFocus={keepInView}
                onKeyDown={e => { if (e.key === 'Enter') addSearch(); }} aria-label={t('checklist.searchAria')} />
            </div>
            <p className="do-checklist__hint">{t('checklist.tapToAdd')}</p>
            <div className="do-checklist__catalog-items">
              {canAddSearch && (
                <div className="do-cat-item">
                  <button className="do-cat-item__pick do-cat-item__pick--new" onClick={addSearch}>
                    <span className="do-cat-item__ico">＋</span>
                    <span className="do-cat-item__name">{t('today.addRow', { q: search.trim() })}</span>
                  </button>
                </div>
              )}
              {catalog.map(item => {
                const on = activeNames.has(normalizeName(item.name));
                return (
                  <div key={item.id} className={`do-cat-item${on ? ' do-cat-item--on' : ''}`}>
                    <button className="do-cat-item__pick" onClick={() => pick(item)}
                      title={on ? t('checklist.removeFromList') : t('checklist.addToList')}>
                      <span className="do-cat-item__ico">{on ? '✓' : '＋'}</span>
                      <span className="do-cat-item__name">{ln(item.name)}</span>
                    </button>
                    <button className="do-cat-item__del" onClick={() => deleteFromCatalog(item)}
                      aria-label={t('checklist.deleteFromChecklist', { name: ln(item.name) })} title={t('checklist.removeFromChecklist')}>✕</button>
                  </div>
                );
              })}
              {catalog.length === 0 && !canAddSearch && <p className="do-checklist__hint">{t('checklist.noMatches')}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** One shop's group on the Shopping list, with its own ＋ to add several items to it. */
function StoreGroup({ storeName, count, adding, text, onText, onToggleAdd, onAdd, onCancel, children }) {
  const { t } = useLang();
  const unassigned = storeName === NO_STORE_LABEL;
  return (
    <div className={`do-store-group${unassigned ? ' do-store-group--none' : ''}`}>
      <div className="do-store-group__head">
        <span className="do-store-group__name">{unassigned ? '📍' : '🏪'} {storeName}</span>
        <span className="do-store-group__count">{count}</span>
        <button className="do-store-group__add" onClick={onToggleAdd}
          aria-label={t('checklist.addItemsTo', { store: storeName })} title={t('checklist.addItemsTo', { store: storeName })}>＋</button>
      </div>
      {adding && (
        <div className="do-store-add">
          <input className="do-store-add__input" value={text} autoFocus placeholder={t('checklist.addItemsPlaceholder')}
            onFocus={keepInView} onChange={e => onText(e.target.value)}
            onBlur={() => { if (!text.trim()) onCancel(); }}
            onKeyDown={e => { if (e.key === 'Enter') onAdd(); if (e.key === 'Escape') onCancel(); }} />
          <button className="do-store-add__btn" onClick={onAdd} disabled={!text.trim()}>{t('common.add')}</button>
        </div>
      )}
      {children}
    </div>
  );
}
